import React, { useState, useEffect } from "react";
import {
  ConditionOwner,
  ConditionShape,
  ConditionStatus,
  ConditionStage,
  DocShape,
  MessageShape,
} from "@eave/shared/types/api";

import moment from "moment-timezone";
import { sortBy } from "lodash";

import APIClient from "web_modules/APIClient";
import DoubleClickToEdit from "web_modules/shared/DoubleClickToEdit";
import { conditionOptions } from "web_modules/underwriter_dashboard/hurdle_editors/Conditions/index";
import { getSessionToken } from "web_modules/shared/session";
import { TRUSTable, Column } from "web_modules/shared/TRUSTable";

import { changeConditionStatus } from "./helpers";
import LinkToDoc from "./LinkToDoc";
import LinkedDocuments from "./LinkedDocuments";
import CommentsModal from "./CommentsModal";
import BatchControls from "../BatchControls";
import { updateCondition } from "./APIHelpers";
import { formatDateString } from "@eave/shared/utils/date-formatter";

const FontAwesome = require("react-fontawesome");
const styles = require("./styles.module.scss");

const stripEmail = (email: string) =>
  email.replace("@helloeave.com", "").replace("@homelight.com", "");

const statusToStatusUpdatedAtHeader = {
  [ConditionStatus.Open]: "Opened By",
  [ConditionStatus.Requested]: "Requested By",
  [ConditionStatus.Received]: "Received By",
  [ConditionStatus.ReadyToReview]: "Ready To Review By",
  [ConditionStatus.Cleared]: "Cleared By",
  [ConditionStatus.NotApplicable]: "Not Applicable By",
};

interface ConditionListProps {
  conditions: ConditionShape[];
  conditionStatus: ConditionStatus;
  app_id: number;
  onUpdate: Function;
  document?: DocShape;
  linkedDocuments?: boolean;
  displayStage?: boolean;
  editable?: boolean;
  displayImportant?: boolean;
  appendConditionComments: (id: number, comment: MessageShape, internal: boolean) => void;
  markCommentRead: (commentID: number, messageID: number) => void;
}

const ConditionList: React.FunctionComponent<ConditionListProps> = ({
  conditionStatus,
  conditions,
  app_id,
  onUpdate,
  document,
  linkedDocuments,
  displayStage,
  editable,
  displayImportant,
  appendConditionComments,
  markCommentRead,
}: ConditionListProps) => {
  // Used for loading spinner + text while waiting for Promises
  const [loading, setLoading] = useState(false);

  // Array of currently selected conditions
  const [selectedConditions, setBatchSelectedItems] = useState([]);

  // Retrieve logged in user to
  const [loggedInUser, setLoggedInUser] = useState({});

  // Converts currently selected conditions into Promises, then fires them off as API calls
  const updateConditionStatus = (batchSelectedConditions, newStatus) => {
    setLoading(true);

    // Update all conditions
    Promise.all(
      conditions
        .filter(c => batchSelectedConditions.includes(c.id))
        .map(condition => {
          const newCondition = changeConditionStatus(
            moment(),
            getSessionToken(),
            condition,
            newStatus,
          );
          return updateCondition(newCondition);
        }),
    )
      .then(() => {
        onUpdate();
        setLoading(false);
        setBatchSelectedItems([]);
      })
      .catch(value => console.error(value));
  };

  // Fetch current user's ID and Name ONCE,
  //   for determining whether a comment was posted by me or someone else
  useEffect(() => {
    APIClient.get("/session").then((resp: any) => {
      setLoggedInUser({ user_id: resp.user_id, name: resp.first_name });
    });
  }, []);

  return (
    <>
      <BatchControls
        buttonLabel={loading ? "Loading..." : "Move to"}
        selectedItems={selectedConditions}
        onSelectionFunction={updateConditionStatus}
        options={conditionOptions.dropdownOrdering}
        enabled={selectedConditions.length > 0}
      />

      <TRUSTable
        data={sortBy(conditions, (condition: ConditionShape) => condition.owner)}
        className={styles.conditions}
        selectedItems={selectedConditions}
        setBatchSelectedItems={setBatchSelectedItems}
        showBatchControls
      >
        {displayImportant && (
          <Column>
            <FontAwesome className={styles.red} name="circle" />
          </Column>
        )}

        <Column header="Status">
          {condition => (
            <DoubleClickToEdit
              disabled={!editable}
              leanRight
              value={condition.status}
              acceptedInput={{
                options: [
                  { label: "Open", value: ConditionStatus.Open },
                  { label: "Requested", value: ConditionStatus.Requested },
                  { label: "Received", value: ConditionStatus.Received },
                  { label: "Ready To Review", value: ConditionStatus.ReadyToReview },
                  { label: "Cleared", value: ConditionStatus.Cleared },
                  { label: "Not Applicable", value: ConditionStatus.NotApplicable },
                ],
              }}
              onSave={status => {
                const newCondition = changeConditionStatus(
                  moment(),
                  getSessionToken(),
                  condition,
                  status,
                );
                return updateCondition(newCondition).then(() => {
                  onUpdate();
                });
              }}
            />
          )}
        </Column>
        <Column header="Owner">
          {condition => {
            return (
              <DoubleClickToEdit
                disabled={!editable}
                value={condition.owner}
                acceptedInput={{
                  options: [
                    { label: "Borrower", value: ConditionOwner.Borrower },
                    { label: "Closing Agent", value: ConditionOwner.ClosingAgent },
                    { label: "Lender", value: ConditionOwner.Lender },
                  ],
                }}
                onSave={owner =>
                  updateCondition({ ...condition, owner }).then(() => {
                    onUpdate();
                  })
                }
              />
            );
          }}
        </Column>
        {displayStage && (
          <Column header="Stage">
            {condition => (
              <DoubleClickToEdit
                disabled={!editable}
                leanRight
                value={condition.stage}
                acceptedInput={{
                  options: [
                    { label: "PreQualification", value: ConditionStage.PreQualification },
                    { label: "PreDecision", value: ConditionStage.PreDecision },
                    { label: "PreClosing", value: ConditionStage.PreClosing },
                    { label: "AtClosing", value: ConditionStage.AtClosing },
                    { label: "PostClosing", value: ConditionStage.PostClosing },
                  ],
                }}
                onSave={stage =>
                  updateCondition({ ...condition, stage }).then(() => {
                    onUpdate();
                  })
                }
              />
            )}
          </Column>
        )}
        <Column header="Condition">
          {condition => (
            <div className={styles.conditionText}>
              <DoubleClickToEdit
                disabled={!editable}
                acceptedInput="textarea"
                rows={5}
                value={condition.condition}
                onSave={value => {
                  return updateCondition({ ...condition, condition: value }).then(() => {
                    onUpdate();
                  });
                }}
              />
            </div>
          )}
        </Column>

        <Column header="Created By">
          {({ created_by_email, created_at }) => (
            <>
              {stripEmail(created_by_email)} on {formatDateString(created_at)}
            </>
          )}
        </Column>
        <Column header={statusToStatusUpdatedAtHeader[conditionStatus]}>
          {({ status_updated_by_email, status_updated_at }) =>
            status_updated_at !== null && status_updated_by_email !== null ? (
              <>
                {stripEmail(status_updated_by_email)} on {formatDateString(status_updated_at)}
              </>
            ) : (
              "-"
            )
          }
        </Column>
        {document && (
          <Column>
            {condition => (
              <LinkToDoc condition={condition} document={document} onUpdate={onUpdate} />
            )}
          </Column>
        )}
        {linkedDocuments && (
          <Column header="Docs">
            {condition => (
              <LinkedDocuments
                documents={condition.documents}
                condition={condition}
                onUpdate={onUpdate}
                appID={app_id}
              />
            )}
          </Column>
        )}
        {document && (
          <Column>
            {condition => (
              <LinkToDoc condition={condition} document={document} onUpdate={onUpdate} />
            )}
          </Column>
        )}
        <Column header="Comments">
          {({
            message_thread_id,
            internal_message_thread_id,
            condition,
            borrower_comment,
            messages,
            id,
          }) => (
            <CommentsModal
              id={id}
              appID={app_id}
              markCommentRead={markCommentRead}
              messageThreadID={message_thread_id}
              internalMessageThreadID={internal_message_thread_id}
              condition={condition}
              comment={borrower_comment}
              messages={messages}
              appendConditionComments={appendConditionComments}
              loggedInUser={loggedInUser}
            />
          )}
        </Column>
      </TRUSTable>
    </>
  );
};

export default ConditionList;
